/**
 * Events Module - Calendar view for events
 * Shows upcoming events sorted by next occurrence
 */

import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SparklesIcon } from '@heroicons/react/24/outline';
import { useObjectsByType, useCreateObject, useUpdateObject, useRetryEventSync } from '@/features/objects/hooks';
import type { Module } from '@/types/modules';
import { useSession } from '@/features/auth/hooks/useSession';
import { useToast } from '@/app/providers/ToastContext';
import { useObjectSelectionListener } from '@/app/providers/ObjectSelectionContext';
import { useReconcileAgentEffects } from '@/app/providers/AgentEffectsContext';
import { useActiveObjectBroadcast } from '@/common/hooks/useActiveObjectBroadcast';
import { ModuleEditModal } from '../_infrastructure/ModuleMenu';
import type { ModuleEditResult } from '../_infrastructure/ModuleMenu';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/query/keys';
import { api } from '@/lib/api';
import { resolveWorkspaceId } from '@/lib/workspace-routing';
import { EventEditModal } from './EventEditModal';
import { CalendarScopeDropdown } from './CalendarScopeDropdown';
import { computeEventFetchWindow } from './eventFetchWindow';
import {
  isToday,
} from './dateTimeUtils';
import { eventOccInstanceId } from './recurrence';
import type { EventObject } from './eventTypes';
import '../_infrastructure/ModuleStyles.css';
import { ModuleEmptyState } from '@/modules/_infrastructure/ModuleEmptyState';
import {
  IntegrationSyncStatusBadge,
} from '@/common/components/IntegrationSyncStatusBadge';
import { useModalOverlayClick } from '@/common/hooks/useModalOverlayClick';
import { useBodyModalOpenClass, useCalendarFullscreenClass } from './hooks/useCalendarBodyClasses';
import { useCalendarSync } from './hooks/useCalendarSync';
import { useCalendarNavigation } from './hooks/useCalendarNavigation';
import { useIsCompactLayout } from '@/common/hooks';
import { IntegrationAccountsModal } from '@/modules/_infrastructure/IntegrationAccountsModal';
import microsoftLogo from '@/common/microsoft-logo.svg';
import { DeleteConfirmationModal } from './components/DeleteConfirmationModal';
import { DisconnectConfirmationModal } from './components/DisconnectConfirmationModal';
import { RecurringEventDialog } from './components/RecurringEventDialog';
import { EventQuickAddModal } from './components/EventQuickAddModal';
import { SyncExplanationModal } from './components/SyncExplanationModal';
import { CalendarOptionsMenu } from './components/CalendarOptionsMenu';
import { CalendarViewSelector } from './views/CalendarViewSelector';
import { getEventEtag } from './lib/getEventEtag';
import {
  getEventsForDate as getEventsForDatePure,
  getEventsOverlappingDay as getEventsOverlappingDayPure,
} from './lib/getEventsForDate';
import { taskToCalendarEvent } from './lib/taskToCalendarEvent';
import { buildCompleteTaskInput } from './lib/completeTaskPatch';
import type { TaskObject } from '../tasks/types';
import { TaskEditModal } from '../tasks/TaskEditModal';
import { useCalendarDnd } from './hooks/useCalendarDnd';
import { useQuickAdd } from './hooks/useQuickAdd';
import { useRecurringEventActions } from './hooks/useRecurringEventActions';
import { useEventDeletion } from './hooks/useEventDeletion';
import { useEventOptionsMenu } from './hooks/useEventOptionsMenu';
import { useCalendarEventData } from './hooks/useCalendarEventData';
import { computeSuspectedDuplicateIds, findSuspectedDuplicateCounterpart } from './lib/calendarEventFilters';
import { resolveCalendarEmptyState } from './calendarEmptyState';
import { isViewButtonActive } from './lib/toolbarActiveState';

interface EventListModuleProps {
  module: Module;
  conversationId?: string;
  filterText?: string | null;
  [legacyProp: string]: unknown;
}

// #1473: Lay out overlapping timed events into side-by-side columns. The
// algorithm lives in event-layout.ts so it can be unit-tested in isolation.

// DraggableEvent / DroppableDay (the dnd-kit wrappers) and the month-grid
// render now live in ./views/MonthView, composed by ./views/CalendarViewSelector
// (the day/week/month switcher) — see the <CalendarViewSelector /> call site below.

export default function EventListModule(props: EventListModuleProps) {
  const { module, filterText, conversationId: explicitConversationId } = props;
  const conversationId = explicitConversationId ?? '';
  const { token, selectedContainer } = useSession();
  const workspaceId = resolveWorkspaceId(selectedContainer);
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const isMobile = useIsCompactLayout();
  const [showAnalysis, setShowAnalysis] = useState(false);

  // Calendar sizing + swipe + expandedView + dateTime clusters (#3279, Phase 4):
  // Day/Week body-height measurement, month-grid cell width, the layout refs the
  // calendar views attach to, the mobile swipe-gesture navigation
  // (calendarContainerRef + swipeRef + touch effect), the expanded/split-view
  // ownership (isExpandedView state + detection effect, moduleRootRef, the ancestor
  // capture/release + style-apply effects), AND the navigation date/view state
  // (calendarDate/timeFilter/expandedDate plus the scroll-to-8AM effect, the
  // timeFilter→config persist effect, and the config→timeFilter sync effect) —
  // extracted verbatim into useCalendarNavigation. Called exactly ONCE here, near
  // the top in a stable position so `calendarDate`/`timeFilter` are available to the
  // fetch window below and `moduleRootRef` to the option-menu positioning effect;
  // the hook owns the state so updates re-render this component identically (no
  // extra render — `calendarDate`/`timeFilter` feed the existing fetchWindow memo
  // straight off the return). The day/week body height measurement stays a
  // useLayoutEffect inside the hook so it fires before paint (#1541). The swipe
  // effect uses the functional setCalendarDate(prev => …) form and keeps its dep
  // array `[isMobile, timeFilter]`. `monthCellWidth` and `swipeRef` are
  // intentionally not destructured (the swipe effect owns `swipeRef` internally; the
  // state still drives renders via the hook's setter); `expandedDate`/
  // `setExpandedDate` are owned in the hook but not yet read here.
  const {
    dayWeekBodyHeight,
    isExpandedView,
    calendarDate,
    setCalendarDate,
    timeFilter,
    setTimeFilter,
    timeGridRef,
    dayWeekContainerRef,
    monthGridRef,
    calendarContainerRef,
    moduleRootRef,
  } = useCalendarNavigation({
    isMobile,
    moduleConfig: module.config,
    moduleId: module.id,
    conversationId,
  });

  // Declared early so they drive the fetch window. Must stay before useObjectsByType.
  const fetchWindow = useMemo(
    () => computeEventFetchWindow(calendarDate, timeFilter),
    [calendarDate, timeFilter],
  );
  const { data, isLoading, error } = useObjectsByType<EventObject>('event', {
    moduleId: module.id,
    limit: 500,
    startAfter: fetchWindow.startAfter,
    startBefore: fetchWindow.startBefore,
    // This module expands recurring masters client-side (see `expandedEvents`),
    // so suppress the server-side expansion — otherwise every occurrence is
    // expanded twice and renders duplicated (the duplicate-DSU bug).
    expandRecurring: false,
  });
  useEffect(() => {
    console.log('[EventListModule] Query state:', {
      isLoading,
      error,
      eventCount: data?.items?.length,
      conversationId,
      data
    });
  }, [data, isLoading, error, conversationId]);

  // Cross-module deep-link (e.g. from the Daily Summary): jump to Day view on
  // the event's date and scroll/flash it into focus.
  //
  // `dateISO` is the occurrence's start (the caller has it). We MUST prefer it
  // over the stored event's startAt: for a recurring event the stored master
  // sits on its first occurrence (e.g. weeks ago), so looking it up here would
  // navigate to the wrong day. Flash matching is base-id tolerant because a
  // server-expanded instance id and the day-grid's id may differ by an
  // occurrence suffix.
  const focusCalendarEvent = useCallback((eventId: string, dateISO?: string) => {
    let dayISO = dateISO;
    if (!dayISO) {
      dayISO = (data?.items ?? []).find((e) => e.id === eventId)?.startAt;
    }
    if (dayISO) {
      const d = new Date(dayISO);
      if (!Number.isNaN(d.getTime())) {
        setCalendarDate(d);
        setTimeFilter('today');
      }
    }
    const idMatches = (elId: string | null): boolean =>
      !!elId && (elId === eventId || elId.startsWith(eventId) || eventId.startsWith(elId));
    let tries = 0;
    const focus = () => {
      const el = Array.from(document.querySelectorAll('[data-event-id]')).find((e) =>
        idMatches(e.getAttribute('data-event-id')),
      );
      if (el) {
        el.scrollIntoView({ block: 'center', behavior: 'smooth' });
        el.classList.add('cal-event-flash');
        setTimeout(() => el.classList.remove('cal-event-flash'), 1800);
        return;
      }
      if (tries++ < 20) setTimeout(focus, 150);
    };
    setTimeout(focus, 240);
  }, [data, setCalendarDate, setTimeFilter]);

  // Object-selection deep-link. `meta.dateISO` (passed by the Daily Summary) is
  // the occurrence's date — preferred so recurring events land on the right day.
  useObjectSelectionListener(
    'event',
    useCallback(
      (eventId: string, meta?: Record<string, unknown>) =>
        focusCalendarEvent(eventId, typeof meta?.dateISO === 'string' ? meta.dateISO : undefined),
      [focusCalendarEvent],
    ),
  );

  const [selectedEvent, setSelectedEvent] = useState<EventObject | null>(null);
  const [editingEvent, setEditingEvent] = useState<EventObject | null>(null);
  // W3 screen-as-scope: announce the open event to the chat so the agent resolves
  // "this" / "move it" / "delete this" to the open event without searching.
  useActiveObjectBroadcast(
    selectedEvent ? { objectType: 'event', objectId: selectedEvent.id, name: selectedEvent.title || 'this event' } : null,
  );
  const [isCreating, setIsCreating] = useState(false);
  const [editingTaskFromCalendar, setEditingTaskFromCalendar] = useState<TaskObject | null>(null);
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [showEventGearMenu, setShowEventGearMenu] = useState(false);
  const [editModuleModalOpen, setEditModuleModalOpen] = useState(false);
  const [showIntegrationAccountsModal, setShowIntegrationAccountsModal] = useState(false);
  // Hide mobile header/footer when modal is open via body class
  const isModalOpen = isCreating || !!selectedEvent || !!editingTaskFromCalendar;
  useBodyModalOpenClass(isModalOpen);

  // Listen for Google account disconnect and toggle events to invalidate caches
  const [deleteConfirmation, setDeleteConfirmation] = useState<{ eventId: string; title: string } | null>(null);
  const [disconnectConfirmation, setDisconnectConfirmation] = useState<{ accountId: string; email: string } | null>(null);

  // Recurring event edit/delete dialog
  const [recurringDialog, setRecurringDialog] = useState<{
    mode: 'edit' | 'delete';
    event: any; // the virtual or exception event
    pendingData?: Record<string, any>; // for edit: the eventData to save
  } | null>(null);

  // handleRecurringAction (the 6-way delete/edit × this/following/all
  // recurrence mutation engine) is owned by useRecurringEventActions, called
  // once below after allEvents / the mutations / handleBack are declared so its
  // deps resolve — matching the inline async closure's original lexical
  // capture (it read those consts only at call time, never during render).
  // timeFilter sync from module.config is now owned by useCalendarNavigation.

  // Options-menu (gear / mobile-options popover) state + the two listeners that
  // drive it (xavior:open-module-options positioning + click-outside-to-close)
  // are owned by useEventOptionsMenu. Called here BEFORE useCalendarSync,
  // which still consumes `showOptionsMenu`. The menu container the JSX renders
  // attaches `optionsMenuRef` so the click-outside guard reads the same node.
  const {
    showOptionsMenu,
    setShowOptionsMenu,
    optionsMenuPosition,
    optionsMenuRef,
  } = useEventOptionsMenu({ moduleId: module.id, moduleRootRef });
  const eventFormSubmitRef = useRef<(() => void) | null>(null);

  // Modal click handling for event form
  const [eventModalContentRef, handleEventModalOverlayClick, handleEventModalMouseDown] = useModalOverlayClick<HTMLDivElement>(() => {
    setSelectedEvent(null);
    setEditingEvent(null);
    setIsCreating(false);
  }, 30);

  // Quick-add cluster (form state + rotating placeholders + handleQuickAdd) is
  // owned by useQuickAdd, called once below AFTER useCalendarSync and the
  // create mutation are declared so its deps resolve. See the hook call site.
  // timeFilter is declared earlier to drive the fetch window
  const showSearch = true;

  // Mobile calendar fullscreen: add class to <html> so CSS can lock scroll
  useCalendarFullscreenClass(isMobile);

  // Drag and drop sensors for calendar events (drag state + handlers live in
  // useCalendarDnd, called once below after `allEvents`/`updateEvent` are declared)
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px movement before drag starts
      },
    })
  );

  // Calendar integration state — Google-account sync orchestration extracted
  // into a colocated hook (#3279 Phase 3). Same hook-call position as the
  // inline cluster it replaces; behavior/refs/listeners preserved byte-for-byte.
  const {
    calendarConnected,
    calendarInitialSyncComplete,
    isSyncingCalendar,
    calendarAccounts,
    loadingAccounts,
    calendarScope,
    setCalendarScope,
    skipCalendarPrompt,
    setSkipCalendarPrompt,
    showSyncExplanationModal,
    setShowSyncExplanationModal,
    expandedAccountIds,
    accountCalendars,
    recentlyUncheckedCalendarIds,
    allWritableCalendars,
    handleCalendarConnect,
    handleGoogleCalendarConnect,
    handleCalendarSync,
    debouncedBackgroundSync,
    handleDisconnectAccount,
    handleToggleAccount,
    handleToggleCalendar,
    handleToggleAccountExpand,
  } = useCalendarSync({
    moduleId: module.id,
    token,
    conversationId,
    queryClient,
    showToast,
    showOptionsMenu,
  });

  // Native-only calendars (team calendars not linked to any provider) skip
  // the connect-provider empty state and behave as if the user already chose
  // "use without syncing".
  const isNativeCalendar = (module.config as import('@/types/modules').CalendarConfig | undefined)?.nativeOnly === true;
  const effectiveSkipPrompt = skipCalendarPrompt || isNativeCalendar;

  // timeFilter sync is now handled by the configTimeFilter useEffect above

  const createEvent = useCreateObject<any, EventObject>('event', undefined, module.id);
  const updateEvent = useUpdateObject<EventObject>('event', undefined, module.id);
  // #3951: retry a failed outbound push for one event. Returns the re-stamped
  // sync state so the modal banner can clear (or report another failure) in place.
  const retryEventSync = useRetryEventSync();
  const handleRetryEventSync = useCallback(
    async (eventId: string): Promise<'synced' | 'error' | undefined> => {
      try {
        const result = await retryEventSync.mutateAsync({ id: eventId });
        return (result as { providerSyncState?: 'synced' | 'error' })?.providerSyncState;
      } catch (err) {
        console.error('[EventListModule] Failed to retry event sync:', err);
        return 'error';
      }
    },
    [retryEventSync],
  );
  const allEvents = data?.items ?? [];

  const calendarEmptyState = resolveCalendarEmptyState({
    connected: calendarConnected,
    initialSyncComplete: calendarInitialSyncComplete,
    hasEvents: allEvents.length > 0,
    loadingAccounts, // #5566: suppress the 'connect' CTA while accounts load
  });

  // Kanban → Calendar: workspace-wide due-dated tasks rendered as virtual events.
  // No moduleId → workspace scope; no date window (the window filters startAt, not
  // dueAt), so fetch a bounded page and map client-side.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: taskData } = useObjectsByType<any>('task', { limit: 500 });
  const taskEvents = useMemo(
    () => ((taskData?.items ?? []) as TaskObject[]).map(taskToCalendarEvent).filter((e): e is EventObject => e !== null),
    [taskData],
  );

  // Calendar → Kanban: completing a task-derived event marks the task done.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  // Pass module.id so the optimistic update runs even when conversationId is
  // empty (the calendar without a conversation) — otherwise completing a task
  // from the calendar has no immediate UI feedback.
  const updateTaskFromCalendar = useUpdateObject<any>('task', conversationId, module.id);
  const handleCompleteTaskEvent = useCallback((event: EventObject) => {
    const input = buildCompleteTaskInput(event);
    if (!input) return;
    updateTaskFromCalendar.mutate(input, {
      onError: (err: any) => {
        if (err?.status === 412) queryClient.invalidateQueries({ queryKey: queryKeys.objects.byType('task') });
      },
    });
  }, [updateTaskFromCalendar, queryClient]);

  // Quick-add form state + rotating placeholders + the create/navigate handler.
  // Called once here, after the Google-sync hook (allWritableCalendars,
  // calendarConnected, debouncedBackgroundSync) and the create mutation are
  // available — matching the inline closure's original lexical capture, which
  // read those consts only at call time. The orchestrator wraps the hook's
  // handleQuickAdd to also close the create modal (setIsCreating(false)) —
  // that modal-visibility state stays owned here.
  const {
    newEventInitialDate,
    setNewEventInitialDate,
    quickAddText,
    setQuickAddText,
    quickAddCalendarId,
    setQuickAddCalendarId,
    quickAddRef,
    getPlaceholder,
    handleQuickAdd: handleQuickAddCore,
  } = useQuickAdd({
    moduleId: module.id,
    calendarDate,
    setCalendarDate,
    allWritableCalendars,
    createEvent,
    calendarConnected,
    debouncedBackgroundSync,
  });
  const handleQuickAdd = () => {
    // Original inline closure called setIsCreating(false) only on the create
    // path (after the `if (!text) return` early-out); the core returns `true`
    // exactly on that path so we preserve that gating.
    if (handleQuickAddCore()) setIsCreating(false);
  };

  if (allEvents.length > 0 && allEvents.some((e: any) => e.rrule)) {
    console.log('[EventList] Events with rrule from API:', allEvents.filter((e: any) => e.rrule).map((e: any) => ({ id: e.id, title: e.title, rrule: e.rrule, startAt: e.startAt })));
  }

  // Drag and drop state + handlers for calendar events (#3279 Phase B).
  // Extracted verbatim into useCalendarDnd; called once here in a stable
  // position after `allEvents`/`updateEvent` so its deps resolve. The
  // callbacks keep their exact dependency arrays inside the hook.
  const { draggedEventId, handleDragStart, handleDragEnd } = useCalendarDnd({
    allEvents,
    updateEvent,
    queryClient,
    conversationId,
  });

  // Event-data derivation pipeline (#3279/#3277): contact/scope/Xavior/text
  // filtering, recurrence expansion, and the connected-empty diagnosis. Owned
  // by useCalendarEventData, which keeps the original useMemo dep arrays and
  // delegates each body to a pure, char-tested helper in lib/calendarEventFilters.
  const { expandedEvents, moduleSyncError, connectedEmptyVariant } = useCalendarEventData({
    allEvents,
    filterText,
    moduleId: module.id,
    calendarScope,
    calendarAccounts,
    accountCalendars,
    recentlyUncheckedCalendarIds,
    // #4298: anchor recurrence expansion to the navigated view so weekly events
    // keep rendering when paging >12 months out.
    calendarDate,
    timeFilter,
  });

  // Merge real calendar events with task-derived virtual events. Task-events skip
  // the Google-scope / hideXavior filters (they are tasks, not feed entries) but
  // still flow through getEventsForDate's day matching.
  const displayEvents = useMemo(() => [...expandedEvents, ...taskEvents], [expandedEvents, taskEvents]);

  // Stale agent-highlight gating: feed each rendered event's live version to the
  // agent-effects coordinator so a concurrent NEWER version (a human edit landing
  // after the agent's) suppresses a stale agent highlight (#agent-fx version-gate).
  const agentReconcileItems = useMemo(
    () => displayEvents.map((e) => ({
      id: e.id,
      // Same occurrence-identity token the chip + the agent-receipt ingest use, so
      // version-gating reconciles the exact key a receipt highlights.
      instanceId: eventOccInstanceId(e),
      version: e.version,
    })),
    [displayEvents],
  );
  useReconcileAgentEffects('event', agentReconcileItems);

  // #3952: same-slot/same-account fuzzy-title suspected-duplicate flag. Computed
  // ONCE over the full expanded set and threaded down through CalendarViewSelector
  // into Day/Week/Month (chips + month "+N more" popover) and into EventEditModal
  // (the resolve affordance). Non-destructive — flag only; the rows stay rendered.
  // Task-derived virtual events (no real object id) can't be duplicates of a
  // synced event in the dedup sense and have no delete path, so they're excluded
  // from the grouper input (a task-event id would never have a counterpart to
  // resolve against anyway).
  const suspectedDuplicateIds = useMemo(
    () => computeSuspectedDuplicateIds(expandedEvents),
    [expandedEvents],
  );

  // Open the account-picker dropdown so the user can re-enable accounts or
  // pick calendars from the empty-state CTAs. Uses the same `xavior:open-module-options`
  // event the gear icon dispatches so positioning logic stays in one place.
  const openAccountPicker = useCallback(() => {
    window.dispatchEvent(new CustomEvent('xavior:open-module-options', {
      detail: { moduleId: module.id },
    }));
  }, [module.id]);

  // Get events for a specific date (non-archived only). Thin closure over the
  // merged display list; the pure filter lives in lib/getEventsForDate.ts.
  const getEventsForDate = (date: Date) => getEventsForDatePure(displayEvents, date);
  const getEventsOverlappingDay = (date: Date) => getEventsOverlappingDayPure(displayEvents, date);

  // Day/week scroll-to-8AM and timeFilter→config persistence are now owned by useCalendarNavigation.

  // Handle click on calendar time slot to create event at that time
  const handleCalendarSlotClick = (date: Date, hour: number) => {
    const clickedDate = new Date(date);
    clickedDate.setHours(hour, 0, 0, 0);
    setEditingEvent(null);
    setQuickAddText('');
    setNewEventInitialDate(clickedDate);
    setIsCreating(true);
  };

  const handleEventClick = (event: EventObject) => {
    setSelectedEvent(event);
  };

  const handleNewEvent = () => {
    setEditingEvent(null);
    setNewEventInitialDate(null);
    setQuickAddText('');
    setIsCreating(true);
  };

  const handleBack = () => {
    setSelectedEvent(null);
    setEditingEvent(null);
    setIsCreating(false);
  };

  // Recurrence mutation engine (delete/edit × this/following/all). Called once
  // here in a stable position after allEvents / createEvent / updateEvent /
  // handleBack so its threaded deps resolve; the dialog STATE stays in the
  // orchestrator (read by RecurringEventDialog below).
  const { handleRecurringAction } = useRecurringEventActions({
    recurringDialog,
    allEvents,
    moduleId: module.id,
    token,
    createEvent,
    updateEvent,
    queryClient,
    setRecurringDialog,
    handleBack,
  });

  // Paired-event delete engine (hotel check-in/out + flight legs). Plain async
  // function owned by useEventDeletion; reads allEvents/token/queryClient live
  // at call time (no memoization), matching the inline closure it replaces.
  const { handleDeleteEvent } = useEventDeletion({ allEvents, token, queryClient });

  // #3952 one-click resolve: from the open EventEditModal, delete the redundant
  // copy of a same-slot suspected-duplicate pair (keep-one / delete-the-other —
  // there is no backend merge route; "merge" == delete the orphan). Picks which
  // copy via findSuspectedDuplicateCounterpart's documented heuristic (prefer the
  // local un-synced orphan). Falls back to a plain delete of the clicked event
  // when no clean counterpart exists (3+ lookalikes), so the action is always
  // well-defined. Reuses the existing handleDeleteEvent path (delete +
  // invalidate objects.byType('event')); recurring masters route through the
  // recurring dialog exactly like the normal delete button does.
  const handleResolveDuplicate = useCallback((event: EventObject) => {
    const pair = findSuspectedDuplicateCounterpart(expandedEvents, event);
    const toRemove = pair?.remove ?? event;
    setSelectedEvent(null);
    if (toRemove._isVirtual || toRemove.recurringEventId || toRemove.rrule) {
      setRecurringDialog({ mode: 'delete', event: toRemove });
    } else {
      handleDeleteEvent(toRemove.id);
    }
  }, [expandedEvents, handleDeleteEvent]);


  // #2725: Don't blank the module while events refetch. `useObjectsByType` in
  // hooks.ts is configured with `placeholderData` (events type), so any
  // previously-rendered events stay on screen during a refetch. We only
  // short-circuit on the *initial* empty load (no data + isLoading) AND keep
  // that path silent — the bare module shell is far less jarring than a
  // "Loading events..." placeholder that wipes the calendar grid every time
  // the user toggles a calendar or switches views.
  if (isLoading && !data) {
    return (
      <div className="module">
        <div className="module-content" aria-busy="true" />
      </div>
    );
  }

  // Event modal component for creating/editing
  return (
    <>
      {/* Event Create/Edit Modal */}
      {isCreating && (
        <EventQuickAddModal
          moduleId={module.id}
          quickAddText={quickAddText}
          setQuickAddText={setQuickAddText}
          quickAddCalendarId={quickAddCalendarId}
          setQuickAddCalendarId={setQuickAddCalendarId}
          quickAddRef={quickAddRef}
          allWritableCalendars={allWritableCalendars}
          newEventInitialDate={newEventInitialDate}
          getPlaceholder={getPlaceholder}
          handleQuickAdd={handleQuickAdd}
          handleBack={handleBack}
          setShowManualEntry={setShowManualEntry}
        />
      )}
      {selectedEvent && (
        <EventEditModal
          event={selectedEvent}
          isSuspectedDuplicate={suspectedDuplicateIds.has(selectedEvent.id)}
          onResolveDuplicate={() => handleResolveDuplicate(selectedEvent)}
          onRetrySync={handleRetryEventSync}
          calendars={
            // For existing events, ensure the event's own calendar is in the list
            // even if the user deselected it in settings
            selectedEvent.providerCalendarId &&
            !allWritableCalendars.some(c => c.id === selectedEvent.providerCalendarId)
              ? [{ id: selectedEvent.providerCalendarId, name: selectedEvent.providerCalendarId, primary: false, accessRole: 'writer' }, ...allWritableCalendars]
              : allWritableCalendars
          }
          onClose={() => setSelectedEvent(null)}
          onSave={(data) => {
            getEventEtag(selectedEvent).then(({ etag }) => {
              updateEvent.mutate({ id: selectedEvent.id, etag, patch: data });
            }).catch(err => console.error('[EventListModule] Failed to update event:', err));
            setSelectedEvent(null);
            if (calendarConnected) debouncedBackgroundSync();
          }}
          onDelete={(id) => {
            setSelectedEvent(null);
            if (selectedEvent._isVirtual || selectedEvent.recurringEventId || selectedEvent.rrule) {
              setRecurringDialog({ mode: 'delete', event: selectedEvent });
            } else {
              handleDeleteEvent(id);
            }
          }}
        />
      )}
      {/* Manual Entry Modal (full form) */}
      {showManualEntry && (
        <EventEditModal
          isNew
          calendars={allWritableCalendars}
          onClose={() => setShowManualEntry(false)}
          onSave={(data) => {
            createEvent.mutate({
              ...data,
              moduleLinks: [{ moduleId: module.id }],
            });
            setShowManualEntry(false);
            if (calendarConnected) debouncedBackgroundSync();
          }}
        />
      )}
      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        confirmation={deleteConfirmation}
        onCancel={() => setDeleteConfirmation(null)}
        onConfirm={async () => {
          await handleDeleteEvent(deleteConfirmation!.eventId);
          setDeleteConfirmation(null);
          setSelectedEvent(null);
          handleBack();
        }}
      />
      <DisconnectConfirmationModal
        confirmation={disconnectConfirmation}
        onCancel={() => setDisconnectConfirmation(null)}
        onConfirm={async () => {
          await handleDisconnectAccount(disconnectConfirmation!.accountId, disconnectConfirmation!.email);
          setDisconnectConfirmation(null);
        }}
      />
      {/* Recurring Event Dialog */}
      <RecurringEventDialog
        mode={recurringDialog?.mode ?? null}
        onAction={(action) => handleRecurringAction(action)}
        onCancel={() => setRecurringDialog(null)}
      />
      {editingTaskFromCalendar && (
        <TaskEditModal
          task={editingTaskFromCalendar as any}
          allTasks={(taskData?.items ?? []) as any}
          onClose={() => setEditingTaskFromCalendar(null)}
          onSave={(patch) => {
            const etag = editingTaskFromCalendar._etag ?? (editingTaskFromCalendar.version != null ? String(editingTaskFromCalendar.version) : '');
            updateTaskFromCalendar.mutate({ id: editingTaskFromCalendar.id, etag, patch: patch as Partial<TaskObject> });
            setEditingTaskFromCalendar(null);
          }}
        />
      )}
    <div ref={moduleRootRef} className="module module-events">
      <div className="module-accent-rail" style={{ background: 'var(--color-border)' }} />
      {/* Module header removed — ModuleRenderer header provides title + gear + expand */}

      {/* Options Dropdown */}
      <CalendarOptionsMenu
        open={showOptionsMenu}
        position={optionsMenuPosition}
        menuRef={optionsMenuRef}
        setShowOptionsMenu={setShowOptionsMenu}
        moduleId={module.id}
        workspaceId={workspaceId}
        token={token}
        conversationId={conversationId}
        showToast={showToast}
        queryClient={queryClient}
        loadingAccounts={loadingAccounts}
        calendarAccounts={calendarAccounts}
        expandedAccountIds={expandedAccountIds}
        accountCalendars={accountCalendars}
        setShowIntegrationAccountsModal={setShowIntegrationAccountsModal}
        handleCalendarConnect={handleCalendarConnect}
        handleToggleAccountExpand={handleToggleAccountExpand}
        handleToggleAccount={handleToggleAccount}
        handleToggleCalendar={handleToggleCalendar}
      />

      {/* Edit Module Modal */}
      {editModuleModalOpen && (
        <ModuleEditModal
          currentLabel={module.label || module.type}
          currentColor={(module.config as any)?.highlightColor ?? null}
          currentIcon={(module.config as any)?.icon ?? null}
          onConfirm={async (result: ModuleEditResult) => {
            setEditModuleModalOpen(false);
            if (!workspaceId || !token) return;
            try {
              const updatedConfig = { ...(module.config || {}), highlightColor: result.highlightColor, icon: result.icon };
              await api.http.patch(`/workspaces/${workspaceId}/conversations/${conversationId}/modules/${module.id}`, { label: result.label, config: updatedConfig }, { authToken: token });
              queryClient.invalidateQueries({ queryKey: queryKeys.conversations.detail(conversationId) });
            } catch (err) {
              console.error('[EventListModule] Failed to update module:', err);
              showToast('Failed to update module', 'error');
            }
          }}
          onCancel={() => setEditModuleModalOpen(false)}
        />
      )}

      {showIntegrationAccountsModal && (
        <IntegrationAccountsModal onClose={() => setShowIntegrationAccountsModal(false)} moduleId={module.id} />
      )}

      {/* Google Calendar Sync Explanation Modal */}
      <SyncExplanationModal
        open={showSyncExplanationModal}
        onCancel={() => setShowSyncExplanationModal(false)}
        onConnect={handleGoogleCalendarConnect}
      />

      {/* Row 1: Day/Week/Month (left) + scope selector (right) */}
      <div className="module-nav">
          <div className="module-nav__primary">
            <div className="module-toolbar-button-group">
              {(['today', 'week', 'month'] as const).map((view) => (
                <button key={view} type="button" className={`module-toolbar-button ${isViewButtonActive(view, timeFilter, showAnalysis) ? 'is-active' : ''}`} aria-pressed={isViewButtonActive(view, timeFilter, showAnalysis)} onClick={() => { setTimeFilter(view); setShowAnalysis(false); }}>
                  {view === 'today' && <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="3" y1="10" x2="21" y2="10"/><line x1="9" y1="2" x2="9" y2="6"/><line x1="15" y1="2" x2="15" y2="6"/></svg>}
                  {view === 'week' && <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="3" y1="10" x2="21" y2="10"/><line x1="9" y1="4" x2="9" y2="22"/><line x1="15" y1="4" x2="15" y2="22"/></svg>}
                  {view === 'month' && <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="3" y1="10" x2="21" y2="10"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="3" y1="15" x2="21" y2="15"/></svg>}
                  <span className="module-toolbar-label" data-text={view === 'today' ? 'Day' : view === 'week' ? 'Week' : 'Month'}>{view === 'today' ? 'Day' : view === 'week' ? 'Week' : 'Month'}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="module-nav__secondary">
            <button
              type="button"
              onClick={() => setShowAnalysis(v => !v)}
              className={`module-toolbar-button ${showAnalysis ? 'is-active' : ''}`}
              title="The Profile"
            >
              <SparklesIcon width={14} height={14} />
              <span className="module-toolbar-label" data-text="Analysis">Analysis</span>
            </button>
            {/* #2726: Account-scope selector — mirrors Gmail's MailboxDropdown.
                Renders only when 2+ accounts are connected; with one (or zero)
                account the selector adds no value and a "filter by inbox" UI
                would just clutter the toolbar. Kept LAST so the dropdown is
                always the rightmost control in the toolbar. */}
            {calendarAccounts.filter(a => a.enabledForModule).length >= 2 && (
              <CalendarScopeDropdown
                selectedScope={calendarScope}
                onSelect={setCalendarScope}
                accounts={calendarAccounts.filter(a => a.enabledForModule).map(a => ({ id: a.id, email: a.email }))}
              />
            )}
          </div>
        </div>
      {/* Empty states — outside module-content for consistent position */}
      {calendarEmptyState === 'connect' && !effectiveSkipPrompt && (
        <div className="module-empty-state">
          <div className="module-empty-state__icon" style={{ background: 'var(--color-accent-muted)' }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--color-brand)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
          </div>
          <div className="module-empty-state__title">AI-powered calendar</div>
          <div className="module-empty-state__description">Connect your calendar to see events here.<br />Xavior will help you stay on top of your schedule.</div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 12 }}>
            <button type="button" className="module-empty-state__button module-empty-state__button--outline" onClick={() => handleCalendarConnect('google')}>
              <img src="/google-logo.svg" width="14" height="14" alt="" />Connect Google
            </button>
            <button type="button" className="module-empty-state__button module-empty-state__button--outline" onClick={() => handleCalendarConnect('microsoft')}>
              <img src={microsoftLogo} width="14" height="14" alt="" />Connect Microsoft
            </button>
          </div>
          <button type="button" className="module-empty-state__secondary" onClick={() => { try { localStorage.setItem(`xavior:skip-calendar-prompt:${module.id}`, '1'); } catch { /* */ } setSkipCalendarPrompt(true); }}>
            Use calendar without syncing...
          </button>
        </div>
      )}
      {calendarEmptyState === 'syncing' && (
        <div className="module-empty-state">
          <div className="module-empty-state__icon" style={{ background: 'var(--color-accent-muted)' }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--color-brand)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
          </div>
          <div className="module-empty-state__title">Syncing your calendar…</div>
          <div className="module-empty-state__description">Your events will appear here shortly.<br />You can keep exploring in the meantime.</div>
        </div>
      )}
      {/* Connected-but-empty surface. When the calendar is genuinely CONNECTED
          the plain "no events yet" hero is suppressed so an empty calendar shows
          JUST THE GRID; only actionable diagnoses (rate-limited / all-disabled /
          no-calendars) still render a hero. But in LOCAL-ONLY mode (the user
          chose "use without syncing" — skipCalendarPrompt && !calendarConnected),
          there is no other guidance and no connect CTA, so we KEEP the
          "No events yet / Create Event" hero — otherwise the grid is blank with
          no way to learn how to add an event. The no-integration "connect your
          calendar" empty state above is a separate surface. */}
      {allEvents.length === 0 && (effectiveSkipPrompt || calendarConnected) && (connectedEmptyVariant !== 'no-events' || (effectiveSkipPrompt && !calendarConnected)) && (
        <ModuleEmptyState
          icon={<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--color-brand)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>}
          iconColor="var(--color-brand)"
          title={
            connectedEmptyVariant === 'rate-limited' ? 'Calendar sync is paused' :
            connectedEmptyVariant === 'all-disabled' ? 'Calendar accounts disabled' :
            connectedEmptyVariant === 'no-calendars' ? 'No calendars selected' :
            isNativeCalendar ? 'Team Calendar' :
            'No events yet'
          }
          description={
            connectedEmptyVariant === 'rate-limited' && moduleSyncError ? (
              <>
                <IntegrationSyncStatusBadge
                  variant="banner"
                  syncErrorCode={moduleSyncError.syncErrorCode}
                  syncError={moduleSyncError.syncError}
                  syncErrorAt={moduleSyncError.syncErrorAt}
                />
                <div style={{ marginTop: 8, fontSize: 13, color: 'var(--color-text-subtle)' }}>
                  Existing events are still here. New events will appear when sync resumes.
                </div>
              </>
            ) : connectedEmptyVariant === 'all-disabled' ? (
              <>Enable a Google account in the account picker to see events.</>
            ) : connectedEmptyVariant === 'no-calendars' ? (
              <>Pick which calendars you want to see in the account picker.</>
            ) : isNativeCalendar ? (
              <>Add events your whole team can see.<br />Long-press a time slot or ask your assistant.</>
            ) : (
              <>Long-press a time slot to create an event,<br />or ask your assistant to add one.</>
            )
          }
          buttonLabel={
            connectedEmptyVariant === 'rate-limited' ? undefined :
            connectedEmptyVariant === 'all-disabled' ? 'Open account picker' :
            connectedEmptyVariant === 'no-calendars' ? 'Pick calendars' :
            'Create Event'
          }
          onButtonClick={
            connectedEmptyVariant === 'rate-limited' ? undefined :
            connectedEmptyVariant === 'all-disabled' ? openAccountPicker :
            connectedEmptyVariant === 'no-calendars' ? openAccountPicker :
            () => {
              const now = new Date();
              now.setMinutes(0, 0, 0);
              now.setHours(now.getHours() + 1);
              setEditingEvent(null);
              setQuickAddText('');
              setNewEventInitialDate(now);
              setIsCreating(true);
            }
          }
        />
      )}
      <div className={`module-content calendar-view-mode ${timeFilter === 'month' ? 'calendar-view-grid' : 'calendar-view-timegrid'}`}>
        <CalendarViewSelector
          calendarDate={calendarDate}
          setCalendarDate={setCalendarDate}
          timeFilter={timeFilter}
          setTimeFilterState={setTimeFilter}
          isMobile={isMobile}
          isExpandedView={isExpandedView}
          showAnalysis={showAnalysis}
          calendarConnected={calendarConnected}
          loadingAccounts={loadingAccounts}
          allEvents={allEvents}
          expandedEvents={displayEvents}
          suspectedDuplicateIds={suspectedDuplicateIds}
          isSyncingCalendar={isSyncingCalendar}
          draggedEventId={draggedEventId}
          dayWeekBodyHeight={dayWeekBodyHeight}
          calendarContainerRef={calendarContainerRef}
          dayWeekContainerRef={dayWeekContainerRef}
          timeGridRef={timeGridRef}
          monthGridRef={monthGridRef}
          sensors={sensors}
          handleDragStart={handleDragStart}
          handleDragEnd={handleDragEnd}
          getEventsForDate={getEventsForDate}
          getEventsOverlappingDay={getEventsOverlappingDay}
          isToday={isToday}
          handleEventClick={handleEventClick}
          handleNewEvent={handleNewEvent}
          handleCalendarSlotClick={handleCalendarSlotClick}
          handleCalendarSync={handleCalendarSync}
          onCompleteTaskEvent={handleCompleteTaskEvent}
          onOpenTaskEvent={(event) => { const t = (taskData?.items ?? []).find((x: any) => x.id === event._sourceTaskId); if (t) setEditingTaskFromCalendar(t as TaskObject); }}
        />
      </div>
    </div>
    </>
  );
}
